import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Mail, Phone, MapPin, Clock, MessageSquare, Send, Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import '@/styles/animations.css';

const Contact = () => {
  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      details: [
        "+48 534 853 254",
      ]
    },
    {
      icon: Mail,
      title: "Email",
      details: [
        "contact@sigmaimmigration.pl",
        "support@sigmaimmigration.pl"
      ]
    },
    {
      icon: MapPin,
      title: "Office",
      details: [
        "ul. Zurawia 32/34",
        "00-515 Warsaw, Poland"
      ]
    },
    {
      icon: Clock,
      title: "Working Hours",
      details: [
        "Monday - Friday: 9:00 - 17:00",
        "Saturday: 10:00 - 14:00"
      ]
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section with Minimalist Animation */}
      <div className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-primary/10 to-background py-24 sm:py-60">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.5))] dark:bg-grid-black/10" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-foreground mb-6 font-alumni animate-fade-in">
              Get in Touch
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind animate-subtle-slide-up" style={{ animationDelay: '200ms' }}>
              Have questions about our immigration services? We're here to help you every step of the way.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Contact Information Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {contactInfo.map((info, index) => {
            const IconComponent = info.icon;
            return (
              <Card 
                key={index}
                className="p-6 text-center border-border hover:shadow-lg transition-all duration-500 hover:scale-105 animate-slide-in-bottom backdrop-blur-sm group"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="mx-auto bg-primary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors animate-float">
                  <IconComponent className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-4 font-alumni">{info.title}</h3>
                {info.details.map((detail, idx) => (
                  <p key={idx} className="text-muted-foreground font-hind mb-2">{detail}</p>
                ))}
              </Card>
            );
          })}
        </div>

        {/* Contact Form and Map Section */}
        <div className="grid md:grid-cols-2 gap-12 mb-20">
          {/* Contact Form */}
          <div className="animate-slide-in-left">
            <Card className="p-8 border-border backdrop-blur-sm">
              <h2 className="text-3xl font-bold text-foreground mb-6 font-alumni">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <Input 
                      placeholder="Your Name" 
                      className="bg-background/50 border-border focus:border-primary transition-colors"
                    />
                  </div>
                  <div>
                    <Input 
                      type="email" 
                      placeholder="Your Email" 
                      className="bg-background/50 border-border focus:border-primary transition-colors"
                    />
                  </div>
                  <div>
                    <Input 
                      placeholder="Subject" 
                      className="bg-background/50 border-border focus:border-primary transition-colors"
                    />
                  </div>
                  <div>
                    <Textarea 
                      placeholder="Your Message" 
                      className="bg-background/50 border-border focus:border-primary transition-colors min-h-[150px]"
                    />
                  </div>
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 group"
                >
                  Send Message
                  <Send className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </form>
            </Card>
          </div>

          {/* Map and Additional Info */}
          <div className="animate-slide-in-right">
            <Card className="p-8 border-border h-full backdrop-blur-sm">
              <div className="mb-8">
                <div className="bg-accent rounded-lg p-4 mb-6">
                  <iframe
                    title="Office Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2443.7977777777776!2d21.0122!3d52.2297!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM1DCsDEzJzQ3LjAiTiAyMcKwMDAnNDQuMCJF!5e0!3m2!1sen!2spl!4v1234567890"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    className="rounded-lg"
                  />
                </div>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4 text-muted-foreground">
                    <MessageSquare className="h-5 w-5 text-primary" />
                    <span>Quick response within 24 hours</span>
                  </div>
                  <div className="flex items-center space-x-4 text-muted-foreground">
                    <Clock className="h-5 w-5 text-primary" />
                    <span>Available for urgent consultations</span>
                  </div>
                </div>
              </div>

              {/* Social Media Links */}
              <div className="border-t border-border pt-6">
                <h3 className="text-xl font-bold text-foreground mb-4 font-alumni">Connect With Us</h3>
                <div className="flex space-x-4">
                  <a href="https://www.facebook.com/share/16VKhUN264/" target="_blank" rel="noopener noreferrer" className="bg-primary/10 p-3 rounded-full hover:bg-primary/20 transition-colors group">
                    <Facebook className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
                  </a>
                  <a href="https://www.instagram.com/sigmaimmigration.pl" target="_blank" rel="noopener noreferrer" className="bg-primary/10 p-3 rounded-full hover:bg-primary/20 transition-colors group">
                    <Instagram className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
                  </a>
                  <a href="https://x.com/sigmaimmigratio" target="_blank" rel="noopener noreferrer" className="bg-primary/10 p-3 rounded-full hover:bg-primary/20 transition-colors group">
                    <Twitter className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
                  </a>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-card rounded-2xl shadow-sm p-12 border border-border overflow-hidden backdrop-blur-sm animate-slide-in-bottom" style={{ animationDelay: '800ms' }}>
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-foreground mb-6 font-alumni">Frequently Asked Questions</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              Find quick answers to common questions about our immigration services
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                q: "How long does the Polish residence card application process take?",
                a: "The typical processing time is 1-3 months, depending on your case complexity and the current workload at the Voivodeship Office. We help streamline the process to minimize delays."
              },
              {
                q: "What documents do I need for my application?",
                a: "Required documents include: valid passport, completed application form, photos, proof of accommodation, health insurance, financial documentation, and purpose-specific documents (employment contract, university acceptance, etc.)."
              },
              {
                q: "Can I work in Poland while my application is being processed?",
                a: "If you have a valid work permit or are from an EU country, you can continue working. For others, specific regulations apply. We provide guidance based on your individual situation."
              },
              {
                q: "What happens if my application is rejected?",
                a: "You have the right to appeal the decision within 14 days. Our Premium Support package includes appeal assistance to help you through this process."
              },
              {
                q: "Do you provide translation services?",
                a: "Yes, our Premium Support package includes document translation services. We work with certified translators to ensure all your documents meet official requirements."
              },
              {
                q: "How much does your service cost?",
                a: "We offer three packages: Basic Support (1099 PLN), Standard Support (1999 PLN), and Premium Support (2999 PLN). Each package includes different levels of assistance to match your needs."
              },
              {
                q: "Can you help with appointment scheduling?",
                a: "Yes, our Standard and Premium packages include appointment scheduling assistance. We help you book your appointment at the appropriate Voivodeship Office."
              },
              {
                q: "What languages do you support?",
                a: "Our team is fluent in Polish and English. We provide support in both languages to ensure clear communication throughout the process."
              }
            ].map((faq, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-6 backdrop-blur-sm animate-slide-in-bottom group hover:shadow-lg transition-all duration-300"
                style={{ animationDelay: `${1000 + index * 100}ms` }}
              >
                <h3 className="text-lg font-bold text-foreground mb-3 font-alumni group-hover:text-primary transition-colors">{faq.q}</h3>
                <p className="text-muted-foreground font-hind group-hover:text-foreground/80 transition-colors">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;
